import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import LoadingDNA from '../components/LoadingDNA';
import { managerMenuItems } from '../components/manager/managerMenu';
import {
  approveExpenseSplit,
  getManagerPendingSplits,
  isExpenseApiError,
  rejectExpenseSplit,
} from '../services/expenseService';

type ExpenseItem = {
  id: string;
  expenseEventId: string;
  type: string;
  merchant: string;
  amount: number;
  dueSla: string;
  policyRule: string;
  status: string;
};

type TripApprovalGroup = {
  tripId: string;
  employeeName: string;
  employeeDept: string;
  totalAmount: number;
  expenseCount: number;
  expenses: ExpenseItem[];
};

const formatINR = (amount: number): string => `₹${amount.toLocaleString('en-IN')}`;

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusBadgeClass = (status: string): string => {
  const value = String(status || '').toUpperCase();
  if (value === 'APPROVED' || value === 'AUTO_APPROVED') return 'bg-emerald-100 text-emerald-800';
  if (value === 'REJECTED') return 'bg-rose-100 text-rose-800';
  return 'bg-amber-100 text-amber-800';
};

const buildTripGroups = async (params?: {
  department?: string;
  category?: string;
}): Promise<TripApprovalGroup[]> => {
  const pendingSplits = await getManagerPendingSplits({
    perPage: 500,
    department: params?.department,
    category: params?.category,
  });
  const grouped = new Map<string, TripApprovalGroup>();

  pendingSplits.forEach((split) => {
    const existing = grouped.get(split.trip_id) || {
      tripId: split.trip_id,
      employeeName: split.employee_name || 'Employee',
      employeeDept: split.department || 'No department',
      totalAmount: 0,
      expenseCount: 0,
      expenses: [],
    };

    existing.expenses.push({
      id: split.split_id,
      expenseEventId: split.expense_event_id,
      type: split.category || 'Expense Share',
      merchant: split.merchant || '-',
      amount: Number(split.split_amount || 0),
      dueSla: split.sla_due_at ? formatDate(split.sla_due_at) : '-',
      policyRule: split.policy_rule || 'Manager approval',
      status: String(split.status || 'PENDING').toUpperCase(),
    });
    existing.expenseCount = existing.expenses.length;
    existing.totalAmount = existing.expenses.reduce((sum, item) => sum + item.amount, 0);
    grouped.set(split.trip_id, existing);
  });

  return Array.from(grouped.values()).sort((a, b) => b.totalAmount - a.totalAmount);
};

const ManagerDashboardPage: React.FC = () => {
  const [tripGroups, setTripGroups] = useState<TripApprovalGroup[]>([]);
  const [openTripId, setOpenTripId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const departmentOptions = useMemo(() => {
    const values = new Set<string>();
    tripGroups.forEach((trip) => values.add(trip.employeeDept));
    return ['All', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [tripGroups]);

  const categoryOptions = useMemo(() => {
    const values = new Set<string>();
    tripGroups.forEach((trip) => trip.expenses.forEach((item) => values.add(item.type)));
    return ['All', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [tripGroups]);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        const groups = await buildTripGroups({
          department: selectedDepartment === 'All' ? undefined : selectedDepartment,
          category: selectedCategory === 'All' ? undefined : selectedCategory,
        });
        setTripGroups(groups);
        setOpenTripId((previous) => previous || groups[0]?.tripId || null);
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load approvals.');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [selectedCategory, selectedDepartment]);

  const totalPendingTrips = tripGroups.length;
  const totalPendingAmount = useMemo(
    () => tripGroups.reduce((sum, trip) => sum + trip.totalAmount, 0),
    [tripGroups],
  );
  const totalExpenses = useMemo(
    () => tripGroups.reduce((sum, trip) => sum + trip.expenseCount, 0),
    [tripGroups],
  );

  const updateTripGroupsAfterAction = (tripId: string, expenseId: string) => {
    setTripGroups((previous) =>
      previous.map((trip) => {
        if (trip.tripId !== tripId) return trip;
        const updatedExpenses = trip.expenses.filter((expense) => expense.id !== expenseId);
        return {
          ...trip,
          expenses: updatedExpenses,
          expenseCount: updatedExpenses.length,
          totalAmount: updatedExpenses.reduce((sum, expense) => sum + expense.amount, 0),
        };
      }).filter((trip) => trip.expenses.length > 0),
    );
  };

  const onApproveExpense = async (tripId: string, expenseId: string) => {
    try {
      setActionMessage(null);
      await approveExpenseSplit(expenseId);
      updateTripGroupsAfterAction(tripId, expenseId);
      setActionMessage('Expense approved successfully.');
    } catch (error) {
      setActionMessage(isExpenseApiError(error) ? error.message : 'Failed to approve expense.');
    }
  };

  const onRejectExpense = async (tripId: string, expenseId: string) => {
    try {
      setActionMessage(null);
      await rejectExpenseSplit(expenseId, 'Rejected by manager from dashboard');
      updateTripGroupsAfterAction(tripId, expenseId);
      setActionMessage('Expense rejected successfully.');
    } catch (error) {
      setActionMessage(isExpenseApiError(error) ? error.message : 'Failed to reject expense.');
    }
  };

  return (
    <AdminShell
      pageTitle="Manager Approvals"
      subtitle="Review pending split approvals from your team."
      menuItems={managerMenuItems}
      profileName="Approving Manager"
      profileRoleTag="MANAGER"
    >
      {errorMessage ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] font-semibold text-red-700">{errorMessage}</p>
      ) : null}
      {actionMessage ? (
        <p className="rounded-lg bg-emerald-50 px-4 py-3 text-[14px] font-semibold text-emerald-700">{actionMessage}</p>
      ) : null}

      {isLoading ? <LoadingDNA /> : null}

      {!isLoading ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">PENDING TRIPS</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b5f77]">{totalPendingTrips}</p>
            </article>
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">PENDING EXPENSES</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b5f77]">{totalExpenses}</p>
            </article>
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">APPROVAL VALUE</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b708b]">{formatINR(totalPendingAmount)}</p>
            </article>
          </div>

          <div className="grid gap-3 rounded-[14px] border border-[#dcebf3] bg-white px-4 py-4 md:grid-cols-2">
            <label className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">
              DEPARTMENT
              <select
                value={selectedDepartment}
                onChange={(event) => setSelectedDepartment(event.target.value)}
                className="mt-2 h-10 w-full rounded-[10px] border border-[#d7e6ef] bg-[#f8fcff] px-3 text-[14px] font-semibold text-[#30576c] outline-none"
              >
                {departmentOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">
              CATEGORY
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="mt-2 h-10 w-full rounded-[10px] border border-[#d7e6ef] bg-[#f8fcff] px-3 text-[14px] font-semibold text-[#30576c] outline-none"
              >
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>

          <article className="rounded-[16px] border border-[#deebf2] bg-white">
            <div className="border-b border-[#deebf2] bg-[#edf7fb] px-5 py-4">
              <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#0b5f77]">Pending Requests</h2>
            </div>

            {tripGroups.length === 0 ? (
              <p className="px-5 py-6 text-[14px] font-semibold text-[#6f8899]">No pending trip approvals found.</p>
            ) : null}

            <div className="divide-y divide-[#e7f1f6]">
              {tripGroups.map((trip) => {
                const isOpen = openTripId === trip.tripId;
                return (
                  <div key={trip.tripId} className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setOpenTripId((prev) => (prev === trip.tripId ? null : trip.tripId))}
                      className="flex w-full items-start justify-between gap-3 text-left"
                    >
                      <div>
                        <p className="text-[12px] font-extrabold tracking-[0.08em] text-[#8eacc0]">TRIP</p>
                        <p className="text-[18px] font-extrabold text-[#0f5f78]">{trip.tripId}</p>
                        <p className="mt-1 text-[14px] font-semibold text-[#658496]">
                          {trip.employeeName} • {trip.employeeDept}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[20px] font-extrabold text-[#133a4d]">{formatINR(trip.totalAmount)}</p>
                        <p className="text-[13px] font-semibold text-[#6d8b9d]">{trip.expenseCount} expenses</p>
                        <p className="mt-1 text-[13px] font-bold text-[#0e7f98]">{isOpen ? 'Hide ▲' : 'View ▼'}</p>
                      </div>
                    </button>

                    {isOpen ? (
                      <div className="mt-4 overflow-x-auto rounded-[12px] border border-[#deebf2] bg-[#fbfdff]">
                        <table className="w-full min-w-[740px]">
                          <thead className="bg-[#f1f9fc]">
                            <tr className="text-left text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                              <th className="px-4 py-3">Expense Type</th>
                              <th className="px-4 py-3">Merchant</th>
                              <th className="px-4 py-3">Amount</th>
                              <th className="px-4 py-3">Due SLA</th>
                              <th className="px-4 py-3">Policy Rule</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trip.expenses.map((expense) => (
                              <tr key={expense.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                                <td className="px-4 py-3">{expense.type}</td>
                                <td className="px-4 py-3">{expense.merchant}</td>
                                <td className="px-4 py-3 font-extrabold text-[#183344]">{formatINR(expense.amount)}</td>
                                <td className="px-4 py-3">{expense.dueSla}</td>
                                <td className="px-4 py-3">{expense.policyRule}</td>
                                <td className="px-4 py-3">
                                  <span className={`rounded-full px-2 py-1 text-[11px] font-extrabold tracking-[0.08em] ${statusBadgeClass(expense.status)}`}>
                                    {expense.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex justify-end gap-2">
                                    <Link
                                      to={`/manager/expense-events/${expense.expenseEventId}`}
                                      className="rounded-full border border-[#b8d6e4] px-4 py-1.5 text-[12px] font-bold text-[#0b6f89] hover:bg-[#edf7fb]"
                                    >
                                      View
                                    </Link>
                                    <button
                                      type="button"
                                      onClick={() => onApproveExpense(trip.tripId, expense.id)}
                                      className="rounded-full bg-[#0d90aa] px-4 py-1.5 text-[12px] font-bold text-white hover:bg-[#0a7f96]"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => onRejectExpense(trip.tripId, expense.id)}
                                      className="rounded-full border border-[#efc7c7] px-4 py-1.5 text-[12px] font-bold text-[#c75a5a] hover:bg-[#fff6f6]"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </article>
        </>
      ) : null}
    </AdminShell>
  );
};

export default ManagerDashboardPage;
